import * as React from 'react'

import { cn } from '#/shared/lib/utils'
import { StarIcon } from '#/shared/ui/icons'

type HistoryRowLayout = 'expanded' | 'compact'

interface HistoryRowProps {
  /** Leading icon column. Omit to render the row without the icon + divider. */
  icon?: React.ComponentType<{ className?: string }>
  title: React.ReactNode
  titleLeading?: React.ReactNode
  meta?: React.ReactNode[]
  action?: React.ReactNode
  layout?: HistoryRowLayout
  /**
   * When set, the leading icon is top-anchored and shifted down by this many
   * pixels instead of centering on the row. Expandable rows use it to freeze
   * the icon on its collapsed-row center so it stays put while the body grows.
   */
  iconOffsetY?: number
  /** Ref to the expanded-layout body, so callers can measure its height. */
  bodyRef?: React.Ref<HTMLDivElement>
  /** Ref to the (animating) title row, so callers can measure its height. */
  titleRowRef?: React.Ref<HTMLDivElement>
  wrapTitle?: boolean
  onClick?: () => void
  className?: string
}

function MetaList({
  items,
  className,
}: {
  items: React.ReactNode[]
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-1.25 text-xs text-gray-500',
        className,
      )}
    >
      {items.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span aria-hidden="true" className="text-gray-300">
              ·
            </span>
          )}
          <span className="flex items-center whitespace-nowrap">{node}</span>
        </React.Fragment>
      ))}
    </div>
  )
}

function CompactBody({
  title,
  titleLeading,
  meta,
  action,
}: Pick<HistoryRowProps, 'title' | 'titleLeading' | 'meta' | 'action'>) {
  const hasMeta = meta && meta.length > 0
  return (
    <div className="flex h-6 min-w-0 flex-1 items-center justify-between gap-2.5">
      <div className="flex min-w-0 items-center gap-1.25 text-sm text-gray-900">
        {titleLeading}
        <span className="truncate">{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1.75 py-0.5">
        {hasMeta && <MetaList items={meta} />}
        {action}
      </div>
    </div>
  )
}

function ExpandedBody({
  title,
  titleLeading,
  meta,
  action,
  wrapTitle,
  bodyRef,
  titleRowRef,
}: Pick<
  HistoryRowProps,
  | 'title'
  | 'titleLeading'
  | 'meta'
  | 'action'
  | 'wrapTitle'
  | 'bodyRef'
  | 'titleRowRef'
>) {
  const hasMeta = meta && meta.length > 0
  return (
    <div ref={bodyRef} className="flex min-w-0 flex-1 flex-col gap-1 py-0.5">
      <div ref={titleRowRef} className="flex w-full items-center gap-1.25">
        <div className="flex min-w-0 flex-1 items-center gap-1.25 text-sm text-gray-900">
          {titleLeading}
          <div
            className={cn(
              'min-w-0 flex-1',
              wrapTitle ? 'whitespace-pre-wrap break-words' : 'truncate',
            )}
          >
            {title}
          </div>
        </div>
        {action && (
          <div className="flex shrink-0 items-center gap-2.5">{action}</div>
        )}
      </div>
      {hasMeta && <MetaList items={meta} />}
    </div>
  )
}

function LeadingIcon({
  icon: Icon,
  offsetY,
}: {
  icon: React.ComponentType<{ className?: string }>
  offsetY: number
}) {
  return (
    <>
      <div
        className={cn(
          'flex shrink-0 items-center',
          offsetY > 0 && 'self-start',
        )}
        style={
          offsetY > 0 ? { transform: `translateY(${offsetY}px)` } : undefined
        }
      >
        <Icon className="size-5 text-gray-500" />
      </div>
      <div className="flex items-center self-stretch">
        <div className="h-full w-px bg-gray-200" />
      </div>
    </>
  )
}

function HistoryRow({
  icon: Icon,
  title,
  titleLeading,
  meta,
  action,
  layout = 'expanded',
  iconOffsetY = 0,
  bodyRef,
  titleRowRef,
  wrapTitle,
  onClick,
  className,
}: HistoryRowProps) {
  const Root = onClick ? 'button' : 'div'
  const clickable = !!onClick
  return (
    <Root
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-stretch gap-2.5 rounded-md border border-gray-200 bg-white py-1.25 text-left',
        layout === 'compact' ? 'pl-2.5 pr-1.25' : 'px-2.5',
        clickable && 'cursor-pointer transition-colors hover:bg-gray-50',
        className,
      )}
    >
      {Icon && <LeadingIcon icon={Icon} offsetY={iconOffsetY} />}
      {layout === 'compact' ? (
        <CompactBody
          title={title}
          titleLeading={titleLeading}
          meta={meta}
          action={action}
        />
      ) : (
        <ExpandedBody
          title={title}
          titleLeading={titleLeading}
          meta={meta}
          action={action}
          wrapTitle={wrapTitle}
          bodyRef={bodyRef}
          titleRowRef={titleRowRef}
        />
      )}
    </Root>
  )
}

function HistoryAppTag({ children = 'App' }: { children?: React.ReactNode }) {
  return <span className="whitespace-nowrap text-primary">{children}</span>
}

interface HistoryViewLinkProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  variant?: 'default' | 'primary'
}

function HistoryViewLink({
  children = 'View',
  variant = 'default',
  className,
  ...props
}: HistoryViewLinkProps) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'cursor-pointer rounded-sm px-1.25 py-0.5 text-sm transition-colors',
        variant === 'default'
          ? 'text-xs text-gray-900 underline underline-offset-2 hover:bg-gray-50'
          : 'text-primary hover:bg-blue-50',
        className,
      )}
    >
      {children}
    </button>
  )
}

function HistoryRating({
  value = 5,
  max = 5,
}: {
  value?: number
  max?: number
}) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <StarIcon
          key={i}
          className={cn('size-3', i < value ? 'text-primary' : 'text-gray-200')}
        />
      ))}
    </span>
  )
}

export { HistoryRow, HistoryAppTag, HistoryViewLink, HistoryRating }
export type { HistoryRowProps, HistoryRowLayout }
